import React, { useMemo, useState } from "react";

import {
    Box,
    Card,
    CardContent,
    Typography,
} from "@material-ui/core";

import PieChartRoundedIcon from "@material-ui/icons/PieChartRounded";
import LinkRoundedIcon from "@material-ui/icons/LinkRounded";
import NoDataCard from "./NoDataCard";
import CardSkeleton from "./CardSkeleton";

/*
 * Fixed categorical order (see the data-viz color formula) — never cycled
 * arbitrarily per render, so a given asset type keeps the same hue across
 * refreshes/filters even as the segment count changes.
 */
const CATEGORICAL = [
    { color: "#2a78d6", lightColor: "#DBEAFE" },
    { color: "#eb6834", lightColor: "#FDE6DC" },
    { color: "#1baf7a", lightColor: "#DCFCE7" },
    { color: "#eda100", lightColor: "#FEF3C7" },
    { color: "#e87ba4", lightColor: "#FCE3EC" },
    { color: "#4a3aa7", lightColor: "#E7E3FA" },
];

const MUTED = { color: "#898781", lightColor: "#EEEEEC" };


const PieChartComp = ({ cardName = "Assets by Type", cardData, totalAssets, linkedCount, linkedLabel = "Linked to active projects", loading }) => {

    const [activeStatus, setActiveStatus] = useState(null);

    const statusData = useMemo(
        () => (cardData || []).map((item, index) => ({
            name: item?.label,
            value: item?.count || 0,
            ...(CATEGORICAL[index] || MUTED),
        })),
        [cardData]
    );


    const total = useMemo(
        () => totalAssets ?? statusData.reduce((sum, item) => sum + item.value, 0),
        [statusData, totalAssets]
    );


    const radius = 105;
    const strokeWidth = 38;

    const circumference =
        2 * Math.PI * radius;


    let accumulated = 0;

    const segments = statusData.map((item) => {

        const percentage =
            total ? item.value / total : 0;

        const segmentLength =
            percentage * circumference;

        const segment = {
            ...item,

            percentage,

            dashArray: `${segmentLength} ${circumference - segmentLength
                }`,

            dashOffset: -accumulated,
        };

        accumulated += segmentLength;

        return segment;
    });


    const activeItem = segments.find(
        (item) => item.name === activeStatus
    );


    const centerValue = activeItem
        ? activeItem.value
        : total;

    const centerLabel = activeItem
        ? activeItem.name
        : "Total Assets";

    const centerPercentage = activeItem
        ? `${(
            activeItem.percentage * 100
        ).toFixed(1)}%`
        : null;


    if (loading) {
        return <CardSkeleton fullHeight />;
    }

    if (!statusData.length) {
        return <NoDataCard title={cardName} />;
    }

    return (
        <Card elevation={0} style={{ height: "100%", width: '100%', display: "flex", flexDirection: "column", border: "1px solid #E5E7EB", borderRadius: 16, background: "#FFFFFF", overflow: "hidden", boxShadow: "0 4px 18px rgba(15, 23, 42, 0.05)" }}>

            <Box style={{ padding: "14px 18px", borderBottom: "1px solid #EEF0F4", display: "flex", alignItems: "center", justifyContent: "space-between" }}>

                <Box style={{ display: "flex", alignItems: "center" }}>

                    <Box style={{ width: 20, height: 20, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", background: "#EEF2FF", color: "#4F46E5", marginRight: 8 }}>
                        <PieChartRoundedIcon fontSize="small" />
                    </Box>
                    <Box>
                        <Typography style={{ fontSize: 16, fontWeight: 800, color: "#172033", lineHeight: 1.2 }}>{cardName}</Typography>
                    </Box>
                </Box>
            </Box>

            <CardContent style={{ padding: "12px 12px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", overflow: "auto" }}>

                <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>


                    <Box style={{ position: "relative", width: 210, height: 210, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg viewBox="0 0 300 300" style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}>


                            <circle cx="150" cy="150" r={radius} fill="none" stroke="#F1F5F9" strokeWidth={strokeWidth} />


                            {segments.map((item) => {

                                const isActive =
                                    activeStatus === item.name;

                                const isInactive =
                                    activeStatus &&
                                    !isActive;

                                return (
                                    <circle
                                        key={item.name}
                                        cx="150"
                                        cy="150"
                                        r={radius}
                                        fill="none"
                                        stroke={item.color}
                                        strokeWidth={isActive ? strokeWidth + 7 : strokeWidth}
                                        strokeDasharray={item.dashArray}
                                        strokeDashoffset={item.dashOffset}
                                        strokeLinecap="butt"
                                        opacity={isInactive ? 0.2 : 1}
                                        style={{ cursor: "pointer", transition: "all 0.2s ease", filter: isActive ? `drop-shadow(0 3px 5px ${item.color}55)` : "none" }}
                                        onMouseEnter={() => setActiveStatus(item.name)}
                                        onMouseLeave={() => setActiveStatus(null)}
                                    />
                                );
                            })}

                        </svg>


                        {/* ================================================= */}
                        {/* DONUT CENTER */}
                        {/* ================================================= */}

                        <Box style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 140, textAlign: "center", pointerEvents: "none" }}>

                            {/* Number */}

                            <Typography style={{ fontSize: activeItem ? 24 : 27, fontWeight: 800, color: activeItem ? activeItem.color : "#172033", lineHeight: 1, transition: "all 0.2s ease" }}>
                                {centerValue}
                            </Typography>


                            {/* Label */}

                            <Typography style={{ marginTop: 6, fontSize: activeItem ? 12 : 13, fontWeight: activeItem ? 700 : 500, color: activeItem ? "#334155" : "#64748B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {centerLabel}
                            </Typography>
                            {activeItem && (
                                <Box style={{ display: "inline-flex", alignItems: "center", marginTop: 7, padding: "4px 9px", borderRadius: 20, background: activeItem.lightColor, color: activeItem.color, fontSize: 11, fontWeight: 700 }}>
                                    {centerPercentage}
                                </Box>
                            )}
                        </Box>

                    </Box>

                    <Box style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "6px 16px" }}>

                        {segments.map((item) => {

                            const isActive =
                                activeStatus === item.name;

                            return (
                                <Box
                                    key={item.name}
                                    onMouseEnter={() => setActiveStatus(item.name)}
                                    onMouseLeave={() => setActiveStatus(null)}
                                    style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", opacity: activeStatus && !isActive ? 0.45 : 1, transition: "all 0.2s ease", background: isActive ? "#F8FAFC" : "transparent", padding: "4px 8px", borderRadius: 6 }}
                                >
                                    <Box style={{ width: 8, height: 8, borderRadius: "50%", background: item.color, boxShadow: `0 0 0 3px ${item.lightColor}`, transition: "all 0.2s ease", flexShrink: 0 }} />
                                    <Typography title={item.name} style={{ fontSize: 13, fontWeight: isActive ? 700 : 600, color: isActive ? item.color : "#273449", transition: "all 0.2s ease", whiteSpace: "nowrap" }}>
                                        {item.name}
                                    </Typography>

                                </Box>
                            );
                        })}

                    </Box>

                </Box>

                {linkedCount != null && (
                    <Box style={{ marginTop: 14, padding: "9px 12px", borderRadius: 9, background: "#F7F7FF", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        {/* Footer Left */}
                        <Box style={{ display: "flex", alignItems: "center" }}>
                            <Box style={{ width: 26, height: 26, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", background: "#FFFFFF", color: "#4F46E5", marginRight: 8 }}>
                                <LinkRoundedIcon fontSize="small" />
                            </Box>
                            <Typography style={{ fontSize: 13, fontWeight: 500, color: "#64748B" }}>
                                {linkedLabel}
                            </Typography>
                        </Box>
                        {/* Footer Right */}

                        <Box style={{ textAlign: "right" }}>
                            <Typography style={{ fontSize: 15, fontWeight: 800, color: "#172033" }}>
                                {linkedCount} of {total}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}
export default PieChartComp;